import * as forge from "node-forge";
import { socketBaseUrl } from "../config/apiConfig";

export interface KeyPair {
  publicKey: string;
  privateKey: string;
}

export interface EncryptionData {
  serverPublicKey: string;
  iv: string;
  encryptionKey: string;
}

export interface MarketDataCallback {
  (data: any): void;
}

export class EncryptDecryptService {
  private keyPair: KeyPair | null = null;
  private serverPublicKey: string | null = null;
  private ivhex: string | null = null;
  private encryptionKey: string | null = null;
  private previousGameName: string | null = null;
  private messageToSocket: any = null;
  private counter: number = 0;
  private marketDataCallbacks: MarketDataCallback[] = [];

  /**
   * Subscribe to market data updates
   */
  subscribeToMarketData(callback: MarketDataCallback): () => void {
    this.marketDataCallbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.marketDataCallbacks.indexOf(callback);
      if (index > -1) {
        this.marketDataCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Update market data and notify subscribers
   */
  updateMarketData(message: any): void {
    this.marketDataCallbacks.forEach((callback) => {
      try {
        callback(message);
      } catch {}
    });
  }

  /**
   * Get message from socket - main message processing function
   */
  getMessageFromSocket(): void {
    this.counter = 0;
    // This will be called by the SocketProvider when messages are received
  }

  /**
   * Process incoming socket message
   */
  async processMessage(message: any, socket?: any): Promise<any> {
    if (
      message === "WebSocket connection closed" ||
      message === "unsubscribed successfully" ||
      message === null
    ) {
      this.updateMarketData(null);
      return null;
    }

    try {
      // Try direct JSON parse first
      const directParse = JSON.parse(message);

      // Check for different server response formats
      if (directParse?.type === 0 || directParse?.type === "SERVER_KEY") {
        // New server format
        this.serverPublicKey = directParse.data || directParse.publicKey;
        this.ivhex = this.decryptString(directParse.iv || directParse.ivHex);
        this.encryptionKey = this.decryptString(directParse.encryptionKey || directParse.key);

        // Send initial message if exists
        if (this.messageToSocket) {
          const msg = this.sendMessageToSocket(this.messageToSocket);
          socket?.send(msg);
        }
        return this.messageToSocket;
      } else {
        this.updateMarketData(directParse);
        return directParse;
      }
    } catch (jsonError) {
      // If not JSON, try to decrypt
      try {
        // New server might send base64 encoded encrypted data
        const decoded = forge.util.decode64(message);

        // Try to parse as JSON first (might be JSON inside)
        try {
          const jsonStr = forge.util.decodeUtf8(decoded);
          const parsed = JSON.parse(jsonStr);
          this.updateMarketData(parsed);
          return parsed;
        } catch {
          // If not JSON, try AES decryption
          if (this.encryptionKey && this.ivhex) {
            const decrypted = this.decryptusingNodeforge(
              forge.util.bytesToHex(decoded),
              this.encryptionKey,
              this.ivhex
            );
            this.updateMarketData(decrypted);
            return decrypted;
          }
        }
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Generate encryption key and establish secure connection
   */
  async generateEncryptionKey(
    gameName: string,
    messageToSocket?: any
  ): Promise<string> {
    this.messageToSocket = messageToSocket;

    if (!this.previousGameName) {
      this.previousGameName = gameName;
    }

    // 1. Generate RSA key pair
    this.keyPair = this.generateKeyPair();

    // 2. Get public key in Base64
    const publicKeyBase64 = btoa(this.keyPair.publicKey);

    // 3. Generate clientSeed (SocketProvider ke hisaab se)
    const generateClientSeed = (): string => {
      if (typeof window !== "undefined" && window.crypto) {
        const arr = new Uint8Array(16);
        window.crypto.getRandomValues(arr);
        return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
      }
      const fallbackArr = new Array(16);
      for (let i = 0; i < 16; i++) {
        fallbackArr[i] = Math.floor(Math.random() * 256);
      }
      return fallbackArr.map((b) => b.toString(16).padStart(2, "0")).join("");
    };

    const clientSeed = generateClientSeed();

    // 5. Generate FINAL URL with public key as token
    let url = "";
    if (messageToSocket?.id?.startsWith("99.")) {
      url = `wss://${socketBaseUrl}/event-1?token=${publicKeyBase64}&clientSeed=${clientSeed}`;
    } else if (messageToSocket?.id?.startsWith("88.")) {
      url = `wss://${socketBaseUrl}/event-1?token=${publicKeyBase64}&clientSeed=${clientSeed}`;
    } else {
      // Default fallback
      url = `wss://${socketBaseUrl}/event-1?token=${publicKeyBase64}&clientSeed=${clientSeed}`;
    }

    return url;
  }

  /**
   * Generate RSA key pair
   */
  private generateKeyPair(): KeyPair {
    const keyPair = forge.pki.rsa.generateKeyPair({ bits: 1024 });

    return {
      publicKey: forge.pki.publicKeyToPem(keyPair.publicKey),
      privateKey: forge.pki.privateKeyToPem(keyPair.privateKey),
    };
  }

  /**
   * Get base URL for different event types
   */

  getBaseUrlofEvent(msg: any): string {

    return "";
  }

  getPublicKeyBase64(): string {
    if (!this.keyPair) {
      throw new Error("Key pair not generated");
    }
    return btoa(this.keyPair.publicKey);
  }

  getKeyPair(): KeyPair | null {
    return this.keyPair;
  }


  /**
   * Send encrypted message to socket
   */
  sendMessageToSocket(message: any): string {
    const msg = JSON.stringify(message);
    const encryptedText = this.encryptUsingNodeForge(
      msg,
      this.encryptionKey!,
      this.ivhex!
    );
    return forge.util.encode64(encryptedText);
    // return msg;
  }

  /**
   * Encrypt data using RSA public key
   */
  async encryptData(data: string, keyPair: KeyPair): Promise<string> {
    try {
      const publicKey = forge.pki.publicKeyFromPem(keyPair.publicKey);
      const encryptedBytes = publicKey.encrypt(data);
      const encryptedData = forge.util.encode64(encryptedBytes);
      return encryptedData;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Decrypt data using RSA private key
   */
  decryptData(items: string[]): string {
    try {
      let concatenatedString = "";
      items.forEach((data: string) => {
        const decryptedString = this.decryptString(data);
        concatenatedString = concatenatedString + decryptedString;
      });
      return concatenatedString;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Decrypt using AES-CBC
   */
  private decryptusingNodeforge(
    encryptedHex: string,
    keyBase64: string,
    ivHex: string
  ): any {
    try {
      const ivBytes = forge.util.hexToBytes(ivHex);

      const decipher = forge.cipher.createDecipher("AES-CBC", keyBase64);
      decipher.start({ iv: ivBytes });
      decipher.update(
        forge.util.createBuffer(forge.util.hexToBytes(encryptedHex))
      );
      decipher.finish();

      const decryptedString = decipher.output.data;

      // Try to parse as JSON, but return raw string if it fails
      try {
        return JSON.parse(decryptedString);
      } catch (jsonError) {
        return decryptedString;
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Encrypt using AES-CBC
   */
  encryptUsingNodeForge(
    data: string,
    keyBase64: string,
    ivHex: string
  ): string {
    const ivBytes = forge.util.hexToBytes(ivHex);

    const cipher = forge.cipher.createCipher("AES-CBC", keyBase64);
    cipher.start({ iv: ivBytes });
    cipher.update(forge.util.createBuffer(data, "utf8"));
    cipher.finish();

    const encryptedBuffer = cipher.output;
    const encryptedHex = forge.util.bytesToHex(encryptedBuffer.data);

    return encryptedHex;
  }

  /**
   * Decrypt string using RSA private key
   */
  decryptString(data: string): string {
    try {
      if (!this.keyPair || !this.keyPair.privateKey) {
        throw new Error("Private key is not available");
      }
      const decryptedBuffer = forge.util.decode64(data);
      const privateKey = forge.pki.privateKeyFromPem(this.keyPair.privateKey);
      const decrypted = privateKey.decrypt(decryptedBuffer);
      return decrypted;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Close existing socket and reset state
   */
  closeExistingSocket(): void {
    this.keyPair = null;
    this.counter = 0;
    this.serverPublicKey = null;
    this.ivhex = null;
    this.encryptionKey = null;
    this.previousGameName = null;
    this.messageToSocket = null;
    this.marketDataCallbacks = [];
  }

  /**
   * Get current encryption state
   */
  getEncryptionState(): {
    hasKeyPair: boolean;
    hasServerKey: boolean;
    hasEncryptionKey: boolean;
  } {
    return {
      hasKeyPair: !!this.keyPair,
      hasServerKey: !!this.serverPublicKey,
      hasEncryptionKey: !!this.encryptionKey,
    };
  }

  /**
   * Get market data observable (similar to Angular service)
   */
  getMarketData(): {
    subscribe: (callback: MarketDataCallback) => () => void;
  } {
    return {
      subscribe: (callback: MarketDataCallback) => {
        return this.subscribeToMarketData(callback);
      },
    };
  }

  hasServerPublicKey(): boolean {
    return !!this.serverPublicKey;
  }

  // Line 327: Add method to reset encryption (for testing)
  resetEncryption(): void {
    this.keyPair = null;
    this.serverPublicKey = null;
    this.ivhex = null;
    this.encryptionKey = null;
  }
}

export default EncryptDecryptService;
